import React from "react";
import {
    CategorizedCheckboxDropdown,
    ClearableCheckboxDropdown,
    Icon,
    Table,
    TimestampFilters,
} from "dyl-components";
import { Table as SemanticTable } from "semantic-ui-react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
    getData,
    onToggleTimeUnit,
    useRangeCalendar,
} from "shared/CustomDateRange/customDateRange";

import {
    KEY_CREATED_ON,
    KEY_OWNER,
    KEY_LAST_MODIFIED,
    KEY_CONVERT,
    KEY_ACCOUNT,
    KEY_ACTIONS,
    KEY_CO_OWNER,
    KEY_DIRECT_CUSTOMER,
} from "./constants";
import { useSelector } from "react-redux";
import SelectAllCheckbox from "shared/SelectAllCheckbox";

const SortActions = ({ direction = false, onToggleSort = () => {} }) => {
    return (
        <div
            onClick={onToggleSort}
            style={{ display: "inline-flex", cursor: "pointer" }}
        >
            <Icon
                name="fa-solid fa-arrow-down-long"
                style={{
                    marginRight: -7,
                    opacity: !direction ? "100%" : "50%",
                }}
            />
            <Icon
                name="fa-solid fa-arrow-up-long"
                style={{ opacity: direction ? "100%" : "50%" }}
            />
        </div>
    );
};
export const ContactsHeader = ({ onFilter, isColumnHidden, isInGroups }) => {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const { group_id } = useParams();
    const { assigned_owners = [], assigned_co_owners= [] }= useSelector(state =>state.contact);
    const ownedByCategories = React.useMemo(() => [
        {
            options: [
                { key: "All", value: "All", text: "All", all: true },
                ...assigned_owners.map(owner => ({
                    key: owner.id,
                    value: owner.id.toString(),
                    text: owner.username,
                }))
            ],
        },
    ], [assigned_owners]);
    
    const coOwnedByCategories = React.useMemo(() => [
        {
            options: [
                { key: "All", value: "All", text: "All", all: true },
                ...assigned_co_owners.map(co_owner => ({
                    key: co_owner.id,
                    value: co_owner.id.toString(),
                    text: co_owner.username,
                }))
            ],
        },
    ], [assigned_co_owners]);

    const {
        timestamp,
        setTimestamp,
        isCustomDateRangeOpen,
        setCustomDateRangeOpen,
        userAuthTimezone,
        dateRange,
        setDateRange,
    } = useRangeCalendar(params);

    const createdOrder =
        (params.get("order_by") === "created" || group_id) &&
        params.get("order") === "asc";
    const lastModifiedOrder =
        params.get("order_by") === "activity" && params.get("order") === "asc";

    const onDateRangeChange = (_, { value }) => {
        setDateRange(value);
    };

    const createdOrderFilter = () => {
        const query = new URLSearchParams(params);
        if (!group_id) {
            const order = !createdOrder ? "asc" : "desc";
            query.set("order", order);
            query.set("order_by", "created");
        } else {
            const order = params.get("order") !== "asc" ? "asc" : "desc";
            query.set("order", order);
        }
        const query_string = query.toString();
        navigate(
            `${group_id ? `/group/${group_id}` : "/contacts"}${
                query_string ? `?${query_string}` : ""
            }`
        );
    };

    const lastModifiedOrderFilter = () => {
        const query = new URLSearchParams(params);
        const order = !lastModifiedOrder ? "asc" : "desc";
        query.set("order", order);
        query.set("order_by", "activity");
        const query_string = query.toString();
        navigate(`/contacts${query_string ? `?${query_string}` : ""}`);
    };

    const { count, contacts = [] } = useSelector((state) => {
        if (!isInGroups) {
            return state.contacts;
        }
        if (group_id === "hotlist") {
            return {
                count: state.groups.hotlistCount,
                contacts: state.groups.hotlistContacts,
            };
        }
        if (group_id === "direct_customer") {
            return {
                count: state.groups.directCustomerCount,
                contacts: state.groups.directCustomerContacts,
            };
        }
        return {
            count: state.groups.contactCount,
            contacts: state.groups.contacts,
        };
    });

    return (
        <Table.Row>
            <SemanticTable.HeaderCell
                width={1}
                className="ContactsTable__SmallFixedWidth"
            >
                <SelectAllCheckbox
                    count={count}
                    data={contacts}
                    type={"contacts"}
                    idProperty={"id"}
                />
            </SemanticTable.HeaderCell>
            {!isColumnHidden(KEY_CREATED_ON) && (
                <SemanticTable.HeaderCell
                    className="ContactsTable__MediumFixedWidth"
                    width={2}
                >
                    <span style={{ marginRight: 5 }}>Created</span>
                    <TimestampFilters
                        timestamp={timestamp}
                        onToggleTimeUnit={(timestamp) =>
                            onToggleTimeUnit(
                                timestamp,
                                setTimestamp,
                                params,
                                navigate,
                                `${
                                    group_id
                                        ? `/group/${group_id}`
                                        : "/contacts"
                                }`,
                            )
                        }
                        isCustomDateRangeOpen={isCustomDateRangeOpen}
                        onOpenCustomDateRange={() => {
                            setCustomDateRangeOpen(true);
                        }}
                        onCloseCustomDateRange={() => {
                            setCustomDateRangeOpen(false);
                        }}
                        dateRange={dateRange}
                        onDateRangeChange={onDateRangeChange}
                        getData={() =>
                            getData({
                                setCustomDateRangeOpen,
                                dateRange,
                                userAuthTimezone,
                                params,
                                navigate,
                                url: `${
                                    group_id
                                        ? `/group/${group_id}`
                                        : "/contacts"
                                }`,
                            })
                        }
                    />

                    <SortActions
                        direction={createdOrder}
                        onToggleSort={() => {
                            createdOrderFilter();
                        }}
                    />
                </SemanticTable.HeaderCell>
            )}
            {!isColumnHidden(KEY_LAST_MODIFIED) && (
                <SemanticTable.HeaderCell
                    className="ContactsTable__MediumFixedWidth"
                    width={1}
                >
                    Last Modified
                    <SortActions
                        direction={lastModifiedOrder}
                        onToggleSort={() => {
                            lastModifiedOrderFilter();
                        }}
                    />
                </SemanticTable.HeaderCell>
            )}
            <SemanticTable.HeaderCell
                className="ContactsTable__LargeFixedWidth"
                width={4}
            >
                Contact Name
            </SemanticTable.HeaderCell>
            {!isColumnHidden(KEY_ACCOUNT) && (
                <SemanticTable.HeaderCell
                    className="ContactsTable__MediumFixedWidth"
                    width={2}
                >
                    <ClearableCheckboxDropdown
                        text="Account"
                        name="account_type"
                        options={[
                            {
                                key: "household",
                                value: "household",
                                text: "Household",
                            },
                            {
                                key: "business",
                                value: "business",
                                text: "Business",
                            },
                        ]}
                        onChange={(_, { value }) => {
                            onFilter(_, { name: "account_type", value });
                        }}
                        value={params.get("account_type")?.split(",")}
                        closeOnLoad={true}
                    />
                </SemanticTable.HeaderCell>
            )}
            {!isColumnHidden(KEY_OWNER) && (
                <SemanticTable.HeaderCell
                    className="ContactsTable__MediumFixedWidth"
                    width={2}
                >
                    <CategorizedCheckboxDropdown
                        text="Owned By"
                        name="owner_id"
                        categories={ownedByCategories}
                        onChange={(_, { value }) => {
                            onFilter(_, { name: "owner_id", value });
                        }}
                        value={params.get("owner_id")?.split(",")}
                        closeOnLoad={true}
                    />
                </SemanticTable.HeaderCell>
            )}
            {!isColumnHidden(KEY_CO_OWNER) && (
                <SemanticTable.HeaderCell
                    className="ContactsTable__MediumFixedWidth"
                    width={2}
                >
                    <CategorizedCheckboxDropdown
                        text="Co-Owned By"
                        name="co_owned_by"
                        categories={coOwnedByCategories}
                        onChange={(_, { value }) => {
                            onFilter(_, { name: "coowner_id", value });
                        }}
                        value={params.get("coowner_id")?.split(",")}
                        closeOnLoad={true}
                    />
                </SemanticTable.HeaderCell>
            )}
            {!isColumnHidden(KEY_CONVERT) && (
                <SemanticTable.HeaderCell
                    className="ContactsTable__MediumFixedWidth"
                    width={2}
                >
                    Convert
                </SemanticTable.HeaderCell>
            )}
            {!isColumnHidden(KEY_DIRECT_CUSTOMER) && (
                <SemanticTable.HeaderCell
                    className="ContactsTable__MediumFixedWidth"
                    width={2}
                >
                    {isInGroups ? (
                        "Direct Customer"
                    ) : (
                        <ClearableCheckboxDropdown
                            text="Direct Customer"
                            name="direct_customer"
                            options={[
                                {
                                    key: "yes",
                                    value: "yes",
                                    text: "Yes",
                                },
                                {
                                    key: "no",
                                    value: "no",
                                    text: "No",
                                },
                            ]}
                            onChange={(_, { value }) => {
                                onFilter(_, { name: "direct_customer", value });
                            }}
                            value={params.get("direct_customer")?.split(",")}
                            closeOnLoad={true}
                        />
                    )}
                </SemanticTable.HeaderCell>
            )}
            {!isColumnHidden(KEY_ACTIONS) && (
                <SemanticTable.HeaderCell width={1} className="ContactsTable__SmallFixedWidth">
                    Actions
                </SemanticTable.HeaderCell>
            )}
        </Table.Row>
    );
};
